import { buildTopologyFaceRef, buildTopologyVertexRef } from '../../../../topology/TopologyReferenceBuilders';
import {
  dedupeEquivalentShapes,
  enumerateShapeItems,
  extractVertexPoint,
} from './common';
import {
  BlendStableTopologyMatcher,
  type BlendStableTopologyCandidate,
  type BlendStableTopologyKind,
  type BlendStableTopologyRuntimeHint,
} from './matcher/BlendStableTopologyMatcher';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;

/**
 * 稳定拓扑解析选项。
 */
interface ResolveStableTopologySelectionOptions {
  /** 当前源体对象 ID，用于校验运行时提示是否同源。 */
  sourceObjectId?: string;
}

/** 面/顶点共享的稳定拓扑匹配器。 */
const stableTopologyMatcher = new BlendStableTopologyMatcher();

/**
 * 解析形体元数据中的拓扑出处表。
 *
 * @param metadata 源体元数据。
 * @returns 可用的拓扑出处表；缺失时返回 null。
 */
function resolveTopologyProvenance(metadata: Record<string, unknown>): SerializedTopologyProvenanceMap | null {
  const candidate = metadata.topologyProvenance as SerializedTopologyProvenanceMap | undefined;
  if (!candidate || !Array.isArray(candidate.faces) || !Array.isArray(candidate.edges)) {
    return null;
  }
  return candidate;
}

/**
 * 将选择项中的运行时提示收窄为面/顶点提示。
 *
 * @param selection 选择项。
 * @param kind 目标子拓扑类型。
 * @returns 面/顶点匹配器可消费的运行时提示。
 */
function resolveRuntimeHint(
  selection: SelectionItemRef,
  kind: BlendStableTopologyKind,
): BlendStableTopologyRuntimeHint | null {
  const runtimeHint: RuntimeTopologyHint | undefined = selection.runtimeTopologyHint;
  if (!runtimeHint || runtimeHint.kind !== kind) {
    return null;
  }
  return {
    kind,
    sourceObjectId: runtimeHint.sourceObjectId,
    topologyIndex: runtimeHint.topologyIndex,
    persistentRef: runtimeHint.persistentRef,
    subEntityId: runtimeHint.subEntityId,
    shapeHash: runtimeHint.shapeHash,
  };
}

/**
 * 按 faceIndex 建立面出处表索引。
 *
 * @param provenance 拓扑出处表。
 * @returns faceIndex 到出处项的映射。
 */
function buildFaceEntryMap(
  provenance: SerializedTopologyProvenanceMap | null,
): Map<number, TopologyProvenanceFaceEntry> {
  return new Map((provenance?.faces ?? []).map((entry) => [entry.faceIndex, entry]));
}

/**
 * 按 vertexIndex 建立顶点出处表索引。
 *
 * @param provenance 拓扑出处表。
 * @returns vertexIndex 到出处项的映射。
 */
function buildVertexEntryMap(
  provenance: SerializedTopologyProvenanceMap | null,
): Map<number, TopologyProvenanceVertexEntry> {
  return new Map((provenance?.vertices ?? []).map((entry) => [entry.vertexIndex, entry]));
}

/**
 * 构建当前源体的面候选快照。
 *
 * @param faces 当前源体面集合。
 * @param provenance 拓扑出处表。
 * @returns 面候选集合。
 */
function buildFaceCandidates(
  faces: unknown[],
  provenance: SerializedTopologyProvenanceMap | null,
): BlendStableTopologyCandidate[] {
  const faceEntryMap = buildFaceEntryMap(provenance);
  return faces.map((_faceShape, faceIndex) => {
    const provenanceEntry = faceEntryMap.get(faceIndex) ?? null;
    return {
      shapeIndex: faceIndex,
      topologyIndex: provenanceEntry?.faceIndex ?? faceIndex,
      subEntityId: provenanceEntry?.subEntityId ?? null,
      provenancePersistentRef: provenanceEntry?.ref ?? null,
      geometryPersistentRef: buildTopologyFaceRef(faceIndex),
    };
  });
}

/**
 * 构建当前源体的顶点候选快照。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param vertices 当前源体去重后的顶点集合。
 * @param provenance 拓扑出处表。
 * @returns 顶点候选集合。
 */
function buildVertexCandidates(
  puppy: PuppyKernel,
  vertices: unknown[],
  provenance: SerializedTopologyProvenanceMap | null,
): BlendStableTopologyCandidate[] {
  const vertexEntryMap = buildVertexEntryMap(provenance);
  return vertices.map((vertexShape, vertexIndex) => {
    const provenanceEntry = vertexEntryMap.get(vertexIndex) ?? null;
    const point = extractVertexPoint(puppy, vertexShape);
    return {
      shapeIndex: vertexIndex,
      topologyIndex: provenanceEntry?.vertexIndex ?? vertexIndex,
      subEntityId: provenanceEntry?.subEntityId ?? null,
      provenancePersistentRef: provenanceEntry?.ref ?? null,
      geometryPersistentRef: point ? buildTopologyVertexRef(point) : null,
    };
  });
}

/**
 * 通过选择项稳定身份解析面 Shape。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 源体 Shape。
 * @param metadata 源体元数据。
 * @param selection 面选择项。
 * @param options 解析选项。
 * @returns 命中的面 Shape。
 */
export function resolveFaceShapeBySelection(
  puppy: PuppyKernel,
  shape: unknown,
  metadata: Record<string, unknown>,
  selection: SelectionItemRef,
  options: ResolveStableTopologySelectionOptions = {},
): unknown {
  if (selection.kind !== 'face') {
    throw new Error(`面拓扑解析仅接受面选择项: ${selection.persistentRef}`);
  }
  const faces = enumerateShapeItems(puppy, shape, 'face');
  const candidates = buildFaceCandidates(faces, resolveTopologyProvenance(metadata));
  const faceIndex = stableTopologyMatcher.resolve({
    kind: 'face',
    sourceObjectId: options.sourceObjectId,
    selection: {
      kind: 'face',
      subEntityId: selection.subEntityId,
      persistentRef: selection.persistentRef,
      runtimeTopologyHint: resolveRuntimeHint(selection, 'face'),
    },
    candidates,
  });
  const faceShape = faces[faceIndex];
  if (!faceShape) {
    throw new Error(`面索引越界，无法解析面引用: ${selection.persistentRef}`);
  }
  return faceShape;
}

/**
 * 通过选择项稳定身份解析顶点 Shape。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param shape 源体 Shape。
 * @param metadata 源体元数据。
 * @param selection 顶点选择项。
 * @param options 解析选项。
 * @returns 命中的顶点 Shape。
 */
export function resolveVertexShapeBySelection(
  puppy: PuppyKernel,
  shape: unknown,
  metadata: Record<string, unknown>,
  selection: SelectionItemRef,
  options: ResolveStableTopologySelectionOptions = {},
): unknown {
  if (selection.kind !== 'vertex') {
    throw new Error(`顶点拓扑解析仅接受顶点选择项: ${selection.persistentRef}`);
  }
  /**
   * 顶点出处表由去重后的顶点集合生成，这里必须使用同一枚举口径。
   * 否则重建时同一个 Puppy CAD 内核 顶点被多个面重复枚举，会造成 vertexIndex 偏移。
   */
  const vertices = dedupeEquivalentShapes(enumerateShapeItems(puppy, shape, 'vertex'));
  const candidates = buildVertexCandidates(puppy, vertices, resolveTopologyProvenance(metadata));
  const vertexIndex = stableTopologyMatcher.resolve({
    kind: 'vertex',
    sourceObjectId: options.sourceObjectId,
    selection: {
      kind: 'vertex',
      subEntityId: selection.subEntityId,
      persistentRef: selection.persistentRef,
      runtimeTopologyHint: resolveRuntimeHint(selection, 'vertex'),
    },
    candidates,
  });
  const vertexShape = vertices[vertexIndex];
  if (!vertexShape) {
    throw new Error(`顶点索引越界，无法解析顶点引用: ${selection.persistentRef}`);
  }
  return vertexShape;
}
